import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, CheckCheck, Send, Sparkles, Settings2, Plus, Loader2, Paperclip } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth } from "../context/AuthContext";
import { useRealtimeEvent } from "../context/RealtimeContext";
import { api, fmtTime, fmtDate, formatErr, streamSSE } from "../lib/api";
import { Attachments, Empty, Online, PageHeader, StatusBadge, UploadButton, inputCls } from "./kit";

function ConvList({ convs, active, onPick }) {
  return (
    <div className="divide-y divide-white/5" data-testid="conversation-list">
      {convs.map((c) => {
        const on = c.type === "direct" ? (c.title === "RushGrind2K Support" ? c.support_online : c.contact?.online) : c.members?.some((m) => m.online);
        return (
          <button key={c.id} onClick={() => onPick(c.id)} className={`w-full text-left px-4 py-3 flex gap-3 items-center hover:bg-white/5 ${active === c.id ? "bg-[#007bff]/15" : ""}`} data-testid={`conversation-item-${c.id}`}>
            <span className="relative icon-ring w-10 h-10 shrink-0 font-cond font-bold">{(c.title || "?")[0]}<span className="absolute -bottom-0.5 -right-0.5"><Online on={on} /></span></span>
            <div className="flex-1 min-w-0">
              <div className="flex justify-between gap-2"><p className="font-semibold text-white truncate">{c.title}</p><span className="text-[11px] text-slate-500 shrink-0">{fmtTime(c.last_message?.created_at)}</span></div>
              <div className="flex justify-between gap-2"><p className="text-xs text-slate-400 truncate">{c.last_message ? `${c.last_message.sender_name}: ${c.last_message.text || "Attachment"}` : c.contact ? c.contact.role : "No messages yet"}</p>
                {c.unread > 0 && <span className="min-w-5 h-5 px-1.5 rounded-full bg-[#00d9ff] text-[#021] text-xs font-bold grid place-items-center" data-testid={`conversation-unread-${c.id}`}>{c.unread}</span>}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ManageGroup({ conv, onChange }) {
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState(conv.title);
  useEffect(() => {
    api.get("/admin/assignable").then((r) => setUsers(r.data));
  }, []);
  const upd = async (body) => {
    try {
      onChange((await api.put(`/conversations/${conv.id}`, body)).data);
      toast.success("Group updated");
    } catch (e) {
      toast.error(formatErr(e));
    }
  };
  const ids = conv.members.map((m) => m.id);
  return (
    <div className="space-y-4" data-testid="manage-group">
      <div className="flex gap-2"><Input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} /><button onClick={() => upd({ title })} className="btn-ghost !py-2">Rename</button></div>
      <div>
        <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">Members</p>
        {conv.members.map((m) => (
          <div key={m.id} className="flex items-center justify-between py-1.5 text-sm"><span className="flex items-center gap-2"><Online on={m.online} />{m.name} <span className="text-slate-500">({m.role})</span></span>
            {m.role !== "admin" && <button onClick={() => upd({ remove_member: m.id })} className="text-rose-300 text-xs" data-testid={`group-remove-${m.id}`}>Remove</button>}</div>
        ))}
      </div>
      <Select onValueChange={(v) => upd({ add_member: v })}>
        <SelectTrigger className={inputCls} data-testid="group-add-member-select"><SelectValue placeholder="Add a member" /></SelectTrigger>
        <SelectContent className="bg-[#060d20]">{users.filter((u) => !ids.includes(u.id)).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>)}</SelectContent>
      </Select>
      <div className="flex flex-wrap gap-2">
        {["open", "closed", "archived"].map((s) => <button key={s} onClick={() => upd({ status: s })} className={`px-3 py-1.5 rounded-lg border capitalize text-sm ${conv.status === s ? "bg-[#007bff] border-[#007bff]" : "border-[#007bff]/30"}`} data-testid={`group-status-${s}`}>{s === "open" ? "Open" : s === "closed" ? "Close" : "Archive"}</button>)}
      </div>
    </div>
  );
}

function Thread({ conv, onBack, onConvChange }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState([]);
  const [typing, setTyping] = useState([]);
  const [text, setText] = useState("");
  const [atts, setAtts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(false);
  const [manage, setManage] = useState(false);
  const endRef = useRef(null);
  const lastTyping = useRef(0);
  const load = useCallback(async () => {
    const { data } = await api.get(`/conversations/${conv.id}/messages`);
    setMsgs(data.messages);
    setTyping(data.typing);
  }, [conv.id]);
  useEffect(() => {
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [load]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [msgs.length]);
  useRealtimeEvent((m) => {
    if ((m.event === "message" || m.event === "read") && m.data.conv_id === conv.id) load();
    if (m.event === "typing" && m.data.conv_id === conv.id) {
      setTyping([m.data.name]);
      setTimeout(() => setTyping([]), 4000);
    }
  });
  const onType = (v) => {
    setText(v);
    if (Date.now() - lastTyping.current > 2500) {
      lastTyping.current = Date.now();
      api.post(`/conversations/${conv.id}/typing`).catch(() => {});
    }
  };
  const send = async (e) => {
    e?.preventDefault();
    if (!text.trim() && !atts.length) return;
    setBusy(true);
    try {
      await api.post(`/conversations/${conv.id}/messages`, { text, attachments: atts });
      setText("");
      setAtts([]);
      load();
    } catch (err) {
      toast.error(formatErr(err));
    }
    setBusy(false);
  };
  const suggest = async () => {
    setAi(true);
    setText("");
    try {
      const fin = await streamSSE("/ai/suggest-reply", { conv_id: conv.id }, (d) => setText((t) => t + d));
      if (fin.error) toast.error("AI suggestion failed");
    } catch {
      toast.error("AI suggestion failed");
    }
    setAi(false);
  };
  const closed = conv.status !== "open";
  const on = conv.type === "direct" ? (user.role === "admin" ? conv.contact?.online : conv.support_online) : null;
  return (
    <div className="flex flex-col h-full min-h-0 w-full flex-1" data-testid="chat-thread">
      <div className="shrink-0 px-4 py-3 border-b border-[#007bff]/20 flex items-center gap-3 min-w-0">
        <button className="md:hidden shrink-0" onClick={onBack} data-testid="chat-back-btn"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-white truncate" data-testid="chat-title">{conv.title}</p>
          <p className="text-xs text-slate-400 flex items-center gap-1.5 overflow-hidden whitespace-nowrap">
            {conv.type === "direct" ? (<><Online on={on} /> {on ? "Online" : "Offline"}{user.role === "admin" && conv.contact ? ` · ${conv.contact.role} · ${conv.contact.email}` : ""}</>) : conv.members.map((m) => <span key={m.id} className="flex items-center gap-1 mr-2"><Online on={m.online} />{m.name}</span>)}
          </p>
        </div>
        {conv.type === "group" && <StatusBadge s={conv.status === "open" ? "active" : conv.status} />}
        {user.role === "admin" && conv.type === "group" && <button onClick={() => setManage(true)} className="p-2 rounded-lg hover:bg-white/5" data-testid="group-manage-btn"><Settings2 className="w-5 h-5" /></button>}
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain scroll-thin p-4 space-y-3" data-testid="chat-messages">
        {msgs.map((m) => {
          const mine = m.sender_id === user.id;
          const read = m.read_by.some((r) => r !== m.sender_id);
          return (
            <div key={m.id} className={`max-w-[85%] sm:max-w-[70%] ${mine ? "ml-auto text-right" : ""}`} data-testid={`chat-message-${m.id}`}>
              {!mine && <p className="text-[11px] text-slate-500 mb-0.5">{m.sender_name} · {m.sender_role}</p>}
              <div className={`inline-block text-left px-3.5 py-2.5 rounded-2xl whitespace-pre-wrap break-words text-sm ${mine ? "bg-[#007bff] text-white rounded-br-sm" : "bg-[#0b1a3d] text-slate-100 rounded-bl-sm"}`}>
                {m.text}<Attachments items={m.attachments} />
              </div>
              <p className={`text-[10px] text-slate-500 mt-0.5 flex items-center gap-1 ${mine ? "justify-end" : ""}`}>{fmtDate(m.created_at)}{mine && (read ? <CheckCheck className="w-3.5 h-3.5 text-[#00d9ff]" data-testid="read-receipt" /> : <Check className="w-3.5 h-3.5" />)}</p>
            </div>
          );
        })}
        {msgs.length === 0 && <p className="text-center text-slate-500 text-sm py-10">No messages yet. Say hi!</p>}
        {typing.length > 0 && <p className="text-xs text-[#00d9ff] italic" data-testid="typing-indicator">{typing.join(", ")} is typing…</p>}
        <div ref={endRef} />
      </div>
      {closed && user.role !== "admin" ? <p className="p-4 text-center text-sm text-slate-500 border-t border-white/5">This conversation is {conv.status}.</p> : (
        <form onSubmit={send} className="shrink-0 p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] border-t border-[#007bff]/20 space-y-2" data-testid="chat-input-row">
          {atts.length > 0 && <div className="flex flex-wrap gap-2 text-xs">{atts.map((a, i) => <span key={i} className="px-2 py-1 rounded bg-[#061232] border border-[#007bff]/30">{a.name} <button type="button" onClick={() => setAtts(atts.filter((_, j) => j !== i))}>✕</button></span>)}</div>}
          <div className="flex gap-2 items-end min-w-0 [&>button]:shrink-0">
            <UploadButton multiple accept="image/*,.pdf,.txt,.zip,video/*" convId={conv.id} onUploaded={(fs) => setAtts([...atts, ...fs])} className="p-2.5 rounded-lg border border-[#007bff]/30 hover:bg-white/5" testid="chat-attach-btn"><Paperclip className="w-5 h-5" /></UploadButton>
            <Textarea rows={1} value={text} onChange={(e) => onType(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) send(e); }} placeholder="Type a message…" className={`${inputCls} min-h-[44px] max-h-40 min-w-0 flex-1 resize-none`} data-testid="chat-input" />
            {user.role === "admin" && <button type="button" onClick={suggest} disabled={ai} title="AI suggest reply" className="p-2.5 rounded-lg border border-[#00d9ff]/40 text-[#00d9ff] hover:bg-[#007bff]/10" data-testid="ai-suggest-btn">{ai ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}</button>}
            <button disabled={busy} className="btn-electric !p-2.5" data-testid="chat-send-btn"><Send className="w-5 h-5" /></button>
          </div>
          {user.role === "admin" && <p className="text-[11px] text-slate-500">AI drafts are suggestions only — review and edit before sending.</p>}
        </form>
      )}
      <Dialog open={manage} onOpenChange={setManage}>
        <DialogContent className="bg-[#050a18] border-[#007bff]/40"><DialogTitle className="font-cond uppercase">Manage group</DialogTitle>{manage && <ManageGroup conv={conv} onChange={onConvChange} />}</DialogContent>
      </Dialog>
    </div>
  );
}

function NewChat({ onOpen }) {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.get("/admin/assignable").then((r) => setUsers(r.data));
  }, []);
  return (
    <Select onValueChange={onOpen}>
      <SelectTrigger className={`${inputCls} w-56`} data-testid="new-chat-select"><Plus className="w-4 h-4" /><SelectValue placeholder="New conversation" /></SelectTrigger>
      <SelectContent className="bg-[#060d20]">{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({u.role}){u.online ? " • online" : ""}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export default function Chat({ type = "direct" }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const [filter, setFilter] = useState("open");
  const active = params.get("c");
  const load = useCallback(async () => {
    let { data } = await api.get("/conversations", { params: { type } });
    if (type === "direct" && user.role !== "admin" && data.length === 0) data = [(await api.post("/conversations/direct", {})).data];
    setConvs(data);
    const first = type === "group" ? data.find((c) => c.status === "open") : data[0];
    if (!active && first && window.innerWidth >= 768) setParams({ c: first.id }, { replace: true });
  }, [type, user.role, active, setParams]);
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);
  useRealtimeEvent((m) => {
    if (["message", "read", "refresh"].includes(m.event)) load();
  });
  const openWith = async (uid) => {
    const { data } = await api.post("/conversations/direct", { user_id: uid });
    await load();
    setParams({ c: data.id });
  };
  const shown = type === "group" ? convs.filter((c) => (filter === "open" ? c.status === "open" : c.status !== "open")) : convs;
  const conv = convs.find((c) => c.id === active);
  return (
    <div data-testid={`chat-page-${type}`}>
      <div className={conv ? "hidden md:block" : ""} data-testid="chat-page-header">
      <PageHeader title={type === "group" ? "Group Chats" : "Messages"} desc={type === "group" ? "Order group chats with customers, grinders and admins" : user.role === "admin" ? "Central inbox for customers and grinders" : "Chat directly with the RushGrind2K team"}>
        {type === "direct" && user.role === "admin" && <NewChat onOpen={openWith} />}
        {type === "group" && ["open", "closed/archived"].map((f) => <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 rounded-full text-sm border capitalize ${filter === f ? "bg-[#007bff] border-[#007bff]" : "border-[#007bff]/30"}`} data-testid={`group-filter-${f}`}>{f}</button>)}
      </PageHeader>
      </div>
      {type === "group" && convs.length === 0 ? <Empty text={user.role === "admin" ? "Create an order group chat from an order's details." : "You haven't been added to any order group chats yet."} /> : (
        <div className="card-neon !p-0 overflow-hidden grid md:grid-cols-[320px_1fr] h-[calc(100vh-15rem)] min-h-[480px]">
          <div className={`border-r border-[#007bff]/20 overflow-y-auto scroll-thin ${conv ? "hidden md:block" : ""}`}><ConvList convs={shown} active={active} onPick={(id) => setParams({ c: id })} /></div>
          <div className={`${conv ? "fixed inset-0 z-40 h-[100dvh] flex flex-col bg-[#050a18] md:static md:z-auto md:h-auto md:bg-transparent" : "hidden md:flex md:flex-col"} min-h-0 min-w-0`} data-testid="chat-pane">
            {conv ? <Thread key={conv.id} conv={conv} onBack={() => setParams({})} onConvChange={(c) => setConvs((cs) => cs.map((x) => (x.id === c.id ? c : x)))} /> : <div className="h-full w-full grid place-items-center text-slate-500">Select a conversation</div>}
          </div>
        </div>
      )}
    </div>
  );
}
